import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';

export function ProfilePage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get('/api/profile/get').then((r) => r.data),
  });

  const profile = profileData?.query?.[0] ?? profileData ?? user;

  const updateMutation = useMutation({
    mutationFn: (data: { firstname: string; lastname: string }) => api.post('/api/profile/update', data),
    onSuccess: () => {
      toast.success('Profile updated successfully.');
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setEditMode(false);
    },
    onError: () => toast.error('Profile update failed. Check required fields and try again.'),
  });

  function handleEditClick() {
    setFirstname(profile?.firstname || '');
    setLastname(profile?.lastname || '');
    setEditMode(true);
  }

  function handleSave() {
    if (!firstname.trim() || !lastname.trim()) {
      toast.error('First name and last name are required.');
      return;
    }
    updateMutation.mutate({ firstname: firstname.trim(), lastname: lastname.trim() });
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading profile...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-3">My Profile</h1>

      {/* Display mode — matches Wappler */}
      {!editMode && (
        <Card className="rounded-[20px]">
          <div className="flex p-6 pt-3">
            {/* Avatar column */}
            <div className="w-1/4 text-center pr-4">
              <img
                src={profile?.avatar ? `/uploads/${profile.avatar}` : '/assets/images/default/avatar.png'}
                alt="Avatar"
                className="rounded mb-2 max-w-full mx-auto"
                style={{ maxHeight: '150px' }}
              />
            </div>
            {/* Details column */}
            <div className="w-3/4">
              <h6 className="text-sm font-semibold text-[#cc0404] mb-4">Details</h6>
              <div className="grid grid-cols-2 gap-x-4">
                <div className="mb-3">
                  <label className="form-label text-sm">First Name</label>
                  <p className="font-normal">{profile?.firstname || '-'}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label text-sm">Last Name</label>
                  <p className="font-normal">{profile?.lastname || '-'}</p>
                </div>
                <div className="mb-3 col-span-2">
                  <label className="form-label text-sm">Email</label>
                  <p className="font-normal">{profile?.email || '-'}</p>
                </div>
                <div className="mb-3">
                  <label className="form-label text-sm">Member since</label>
                  <p className="font-normal">{formatDate(profile?.modified || profile?.created)}</p>
                </div>
              </div>
              <div className="text-end">
                <Button className="mt-3" onClick={handleEditClick}>Edit Profile</Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Edit mode — matches Wappler */}
      {editMode && (
        <Card className="rounded-[20px] mt-4">
          <CardContent className="p-6">
            <h6 className="text-sm font-semibold text-[#cc0404] mb-4">Edit Profile Information</h6>

            <div className="mb-3">
              <label className="form-label text-sm mb-1">First Name</label>
              <input
                type="text"
                className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                placeholder="Enter your first name"
                maxLength={70}
              />
            </div>

            <div className="mb-3">
              <label className="form-label text-sm mb-1">Last Name</label>
              <input
                type="text"
                className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                placeholder="Enter your last name"
                maxLength={70}
              />
            </div>

            <div className="text-end mt-3">
              <Button variant="secondary" className="mr-2" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}